// ---- V3 API URLS ---
import Config from '@/config';
import { getRestEndpoint } from '@/runtimeConfig';

export const BASE_URL_V3 = Config.BASEURL_V3 || '';
// Resolved lazily: a module-level constant captures the BUILD-TIME value, which
// is what pointed review apps at the environment the bundle was compiled
// against rather than their own API.
export const getBaseRubyUrl = (): string => getRestEndpoint();
export const BASE_RUBY_URL = Config.BASE_RUBY_URL || '';

export const privacyPolicyUrl = 'https://gravywork.com/privacy-policy';

export const V3Endpoints = {
  GET_USER: '/v1/users/me',
  GET_HEALTH: '/v1/health',
  REQUEST_APP_ACCESS: '/v1/users/request-access/client-app',
  GRANT_APP_ACCESS: '/v1/users/grant-access/client-app',
  REVOKE_APP_ACCESS: '/v1/users/revoke-access/client-app',
  UPDATE_CLIENT_STATUS: '/v1/customers/status/update',
  GET_CHATGROUP_LIST: '/v1/chatgroup/list',
  GET_CHATGROUP_BY_ID: '/v1/chatgroup/',
  VERIFY_ANNOUNCEMENT: '/v1/announcement/verify',
  GET_CHATGROUP_LIST_USERS: '/v1/chatgroup/list/users/',
  SEARCH_CHAT_USERS: '/v1/chatgroup/search',
  CREATE_CHAT_GROUP: '/v1/chatgroup/create',
  GET_CUSTOMERS_COUNT: '/v1/customers/count',
  GET_CHATGROUP_IDS: '/v1/chatgroup/list/ids',
  CREATE_ANNOUNCEMENT: '/v1/announcement',
  CREATE_BULK_CHAT: '/v1/chatgroup/bulk/create',
  CREATE_GROUP_CHAT: 'v1/chatgroup/create/group',
  GET_INVOICE_LINK: '/v1/invoices/',
  DELETE_CUSTOMER_ACCOUNT: '/v1/customers/account/close/',
  GET_WORKERS_COUNT: '/v1/jobworker/worker-count',
  GET_WORKERS_LIST: '/v1/jobworker/valid-workers',
  GET_ACCOUNT_SKILLS: '/v1/customers/account/{account_id}/skills',
  GET_CUSTOMER_ADDRESSES: '/v1/customers/addresses/',
  CHECK_APP_VERSION: '/v1/versions/check/',
  UPSERT_ADDITIONAL_EMAILS: '/v1/notification-subscription/',
};

export const ApiEndpoints = {
  GET_WORKERS_COUNT: '/api/workers/worker_count',
  GET_WORKERS_LIST: '/api/workers/valid_workers',
};

// Graphy admin endpoints (replacing v3 chat APIs as part of v3 decommission).
export const GraphyAdminEndpoints = {
  CREATE_CHAT: '/api/admin/chats',
  GET_ADMIN_CHATS: '/api/admin/chats',
  GET_CHAT_BY_ID: (id: string) => `/api/admin/chats/${id}`,
  // Current admin's resolved flag -> variant map from graphy's native A/B
  // feature-flag engine (FeatureFlags.for_user). Read through useFeatureFlag.
  GET_FEATURE_FLAGS_ME: '/api/admin/feature_flags/me',
  FEATURE_FLAGS: '/api/admin/feature_flags',
  // Keys are percent-encoded at this one choke point: a key containing a
  // URL-special character ("a?b", "a b") interpolated raw silently requests
  // the WRONG route (query-string truncation / unroutable path). Dotted keys
  // pass through unchanged — encodeURIComponent leaves "." and "_" alone.
  FEATURE_FLAG_BY_KEY: (key: string) =>
    `/api/admin/feature_flags/${encodeURIComponent(key)}`,
  FEATURE_FLAG_EXPERIMENTS: (flagKey: string) =>
    `/api/admin/feature_flags/${encodeURIComponent(flagKey)}/experiments`,
  FEATURE_FLAG_EXPERIMENT_BY_ID: (flagKey: string, id: number) =>
    `/api/admin/feature_flags/${encodeURIComponent(flagKey)}/experiments/${id}`,
  FEATURE_FLAG_EXPERIMENT_START: (flagKey: string, id: number) =>
    `/api/admin/feature_flags/${encodeURIComponent(flagKey)}/experiments/${id}/start`,
  FEATURE_FLAG_EXPERIMENT_PAUSE: (flagKey: string, id: number) =>
    `/api/admin/feature_flags/${encodeURIComponent(flagKey)}/experiments/${id}/pause`,
  FEATURE_FLAG_EXPERIMENT_COMPLETE: (flagKey: string, id: number) =>
    `/api/admin/feature_flags/${encodeURIComponent(flagKey)}/experiments/${id}/complete`,
  // First Responder shadow-pilot command centre (GRAVY-10373). Read surface
  // plus one action that runs a SHADOW evaluation now — START/STOP is the
  // audited first_responder_pilot feature flag above, deliberately not a
  // separate endpoint.
  FIRST_RESPONDER: '/api/admin/first_responder',
  FIRST_RESPONDER_JOB_BY_ID: (jobId: number) =>
    `/api/admin/first_responder/jobs/${jobId}`,
  FIRST_RESPONDER_TICK: '/api/admin/first_responder/tick',
  // First Responder policy admin console (P3-E, GRAVY-10373 connected pass).
  // Separate read/write surface from the legacy shadow pilot endpoints above
  // — mode/safety overview, policy read/draft/activate, paginated audit
  // history, and (GRAVY-10373 Task 7) the real jobs/job-detail/workers/attention surface.
  FIRST_RESPONDER_CONSOLE: '/api/admin/first_responder/console',
  FIRST_RESPONDER_CONSOLE_POLICY: '/api/admin/first_responder/console/policy',
  FIRST_RESPONDER_CONSOLE_POLICY_DRAFT:
    '/api/admin/first_responder/console/policy/draft',
  FIRST_RESPONDER_CONSOLE_POLICY_ACTIVATE:
    '/api/admin/first_responder/console/policy/activate',
  FIRST_RESPONDER_CONSOLE_POLICY_AUDITS:
    '/api/admin/first_responder/console/policy/audits',
  // GRAVY-10373 Task 3: the real OFF/SHADOW/LIVE mode transition endpoint. Replaces the
  // audited-feature-flag PATCH the console used for OFF<->SHADOW only.
  FIRST_RESPONDER_CONSOLE_MODE: '/api/admin/first_responder/console/mode',
  // GRAVY-10373 Task 7: the four endpoints that retire the legacy overview adapter. `:id` on the
  // detail/workers routes is the JOB id (WireJobRow.job_id), never the run id (WireJobRow.id).
  FIRST_RESPONDER_CONSOLE_JOBS: '/api/admin/first_responder/console/jobs',
  FIRST_RESPONDER_CONSOLE_JOB_BY_ID: (jobId: string) =>
    `/api/admin/first_responder/console/jobs/${jobId}`,
  FIRST_RESPONDER_CONSOLE_JOB_WORKERS: (jobId: string) =>
    `/api/admin/first_responder/console/jobs/${jobId}/workers`,
  FIRST_RESPONDER_CONSOLE_ATTENTION:
    '/api/admin/first_responder/console/attention',
  // Shared with the dashboard explorer's GET (verb-distinct route on the
  // same path — graphy's ingest controller owns POST, the dashboard
  // controller owns GET). See analyticsQueue/transport.ts (POST) and
  // analyticsDashboardAPI.ts (GET).
  ANALYTICS_EVENTS: '/api/admin/analytics/events',
  ANALYTICS_DASHBOARD_SUMMARY: '/api/admin/analytics/summary',
  ANALYTICS_DASHBOARD_HEALTH: '/api/admin/analytics/health',
  ANALYTICS_DASHBOARD_EVENT_NAMES: '/api/admin/analytics/event_names',
  // BI rollups (analytics_daily_metrics) — a different data class from the
  // raw-event endpoints above. See Analytics::Dashboard::MetricsService.
  ANALYTICS_DASHBOARD_METRICS: '/api/admin/analytics/metrics',
  ANALYTICS_DASHBOARD_METRIC_CATALOG: '/api/admin/analytics/metric_catalog',
  // GRAVY-10436: activation funnel stage counts for the rebuilt GravyWorkers page.
  GET_WORKERS_ACTIVATION_FUNNEL: '/api/admin/workers/activation_funnel',
};
